import * as CL from '@design-system/component-library';
import classNames from 'classnames';
import { useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { dsClass } from '../../../../common/constants/dsClasses';
import { fetchPickupPoints } from '../../../../common/fetch';
import { noSearchResults, t } from '../../../../common/i18n';
import { PostalCode } from '../../../../common/react-hook-form/fields/PostalCode';
import { formatSum } from '../../../../common/utils/priceUtils';
import { POSTAL_CODE_REGEX } from '../../../../common/utils/validationUtils';
import { DeliveryModeType, type PlaceOrderFormData, getDeliveryModes } from '../../placeOrderTypes';
import { type MergedDeliveryMethod, filterAvailableMethods } from '../../utils/placeOrderUtils';
import { PickupPointList } from './PickupPointList';
import type { PickupPoint } from '../../../../generated/api/pickupPoint';

import './DeliveryMethodList.scss';

const INITIAL_FETCH_COUNT = 20;
const DISPLAY_INCREMENT = 5;

interface DeliveryMethodListProps {
  deliveryMethods: MergedDeliveryMethod[];
  onClearError?: () => void;
  allowedModes: DeliveryModeType[];
}

export const DeliveryMethodList = ({ deliveryMethods, onClearError, allowedModes }: DeliveryMethodListProps) => {
  const { setValue, watch } = useFormContext<PlaceOrderFormData>();
  const selectedDeliveryType = watch('delivery.deliveryType');
  const pickupPointPostalCode = watch('delivery.pickupPointPostalCode');
  const savedPickupPoint = watch('delivery.pickupPoint');

  const [pickupPoints, setPickupPoints] = useState<PickupPoint[]>([]);
  const [selectedPickupPoint, setSelectedPickupPoint] = useState<string | undefined>(savedPickupPoint?.id);
  const [displayedCount, setDisplayedCount] = useState(DISPLAY_INCREMENT);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (savedPickupPoint?.id && savedPickupPoint.id !== selectedPickupPoint) {
      setSelectedPickupPoint(savedPickupPoint.id);
    }
  }, [savedPickupPoint?.id, selectedPickupPoint]);

  const availableMethods = filterAvailableMethods(deliveryMethods, allowedModes);
  const availableModes = getDeliveryModes().filter(mode => availableMethods.some(m => m.mode === mode.type));

  const currentMethod = availableMethods.find(m => m.deliveryTypeCode === selectedDeliveryType);
  const selectedMode = currentMethod?.mode ?? availableMethods[0]?.mode ?? DeliveryModeType.ADDRESS;
  const currentMethods = availableMethods.filter(m => m.mode === selectedMode);

  useEffect(() => {
    setDisplayedCount(DISPLAY_INCREMENT);

    const isCollectPointMode = currentMethod?.mode === DeliveryModeType.COLLECT_POINT;
    const hasValidPostalCode = pickupPointPostalCode && POSTAL_CODE_REGEX.test(pickupPointPostalCode);
    const shouldFetch = hasValidPostalCode && isCollectPointMode && currentMethod.service;

    if (shouldFetch) {
      setIsLoading(true);
      fetchPickupPoints(pickupPointPostalCode, currentMethod.service!, INITIAL_FETCH_COUNT)
        .then(response => {
          const points = response.pickupPoints || [];
          if (savedPickupPoint && !points.some(p => p.id === savedPickupPoint.id)) {
            setPickupPoints([savedPickupPoint, ...points]);
          } else {
            setPickupPoints(points);
          }
        })
        .catch(error => {
          console.warn('Failed to fetch pickup points:', error);
          setPickupPoints([]);
        })
        .finally(() => setIsLoading(false));
    } else if (savedPickupPoint && isCollectPointMode) {
      setPickupPoints([savedPickupPoint]);
    } else {
      setPickupPoints([]);
    }

    if (!savedPickupPoint?.id) {
      setSelectedPickupPoint(undefined);
      setValue('delivery.pickupPoint', undefined);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pickupPointPostalCode, selectedDeliveryType]);

  const selectMethod = (method: MergedDeliveryMethod) => {
    if (selectedDeliveryType !== method.deliveryTypeCode) {
      setSelectedPickupPoint(undefined);
      setValue('delivery.pickupPoint', undefined);
    }
    setValue('delivery.deliveryType', method.deliveryTypeCode);
    setValue('delivery.deliveryPriceInCents', method.price);
    onClearError?.();
  };

  const selectMode = (mode: DeliveryModeType) => {
    const method = availableMethods.find(m => m.mode === mode);
    if (!method) {
      return;
    }
    selectMethod(method);
  };

  const selectPickupPoint = (pointId: string) => {
    const point = pickupPoints.find(p => p.id === pointId);
    if (!point) {
      return;
    }
    setSelectedPickupPoint(pointId);
    setValue('delivery.pickupPoint', point);
  };

  const clearPickupPoint = () => {
    setSelectedPickupPoint(undefined);
    setValue('delivery.pickupPoint', undefined);
  };

  const loadMorePickupPoints = () => {
    const newCount = displayedCount + DISPLAY_INCREMENT;
    setDisplayedCount(newCount);

    if (newCount > pickupPoints.length && currentMethod?.service && pickupPointPostalCode) {
      setIsLoading(true);
      fetchPickupPoints(pickupPointPostalCode, currentMethod.service, pickupPoints.length + DISPLAY_INCREMENT)
        .then(response => setPickupPoints(response.pickupPoints || []))
        .catch(error => console.warn('Failed to fetch pickup points:', error))
        .finally(() => setIsLoading(false));
    }
  };

  return (
    <div className="of-place-order-delivery-method">
      <p>{t.BTOF('Choose delivery method')}</p>
      <CL.Disclaimer
        disclaimerType="info"
        className={dsClass.MARGIN_BOTTOM_2}
        icon={<CL.Icon color="blue" icon="information" />}
        text={t.D99O('Orders placed before 12 PM on weekdays will be usually delivered on the next working day.')}
      />

      {availableModes.length > 1 && (
        <CL.SelectGroup
          className={`of-place-order-delivery-method__modes ${dsClass.MARGIN_BOTTOM_4}`}
          products={availableModes.map(mode => ({
            id: mode.type,
            name: mode.name,
            icon: <CL.Icon icon={mode.icon} color="black" type="light" />,
          }))}
          value={selectedMode}
          onChange={(id: string) => selectMode(id as DeliveryModeType)}
        />
      )}

      <div className="of-place-order-delivery-method__list">
        {currentMethods.map(method => {
          const isSelected = selectedDeliveryType === method.deliveryTypeCode;
          const isCollectPoint = isSelected && method.mode === DeliveryModeType.COLLECT_POINT;
          const hasValidPostalCode = pickupPointPostalCode && POSTAL_CODE_REGEX.test(pickupPointPostalCode);

          return (
            <div
              key={method.deliveryTypeCode}
              className={classNames('of-place-order-delivery-method__item', { selected: isSelected })}
            >
              <button
                type="button"
                className="of-place-order-delivery-method__item-button"
                onClick={() => selectMethod(method)}
              >
                <div className="of-place-order-delivery-method__item-content">
                  <div className="of-place-order-delivery-method__item-info">
                    <div className="of-place-order-delivery-method__item-header">
                      <strong>{method.header}</strong>
                    </div>
                    {method.description && (
                      <div className="of-place-order-delivery-method__item-description">{method.description}</div>
                    )}
                  </div>
                  <div className="of-place-order-delivery-method__item-price">
                    <strong>{formatSum(method.price)}</strong>
                  </div>
                  <div className={`of-place-order-delivery-method__item-logo ${method.logo}`} />
                </div>
              </button>

              {isCollectPoint && (
                <>
                  <div className={classNames('of-place-order-delivery-method__postal-code-input', dsClass.WIDTH_40)}>
                    <PostalCode name="delivery.pickupPointPostalCode" required={false} />
                  </div>

                  <PickupPointList
                    points={pickupPoints}
                    selectedPointId={selectedPickupPoint}
                    displayedCount={displayedCount}
                    initialFetchCount={INITIAL_FETCH_COUNT}
                    isLoading={isLoading}
                    logo={method.logo}
                    onSelect={selectPickupPoint}
                    onClear={clearPickupPoint}
                    onLoadMore={loadMorePickupPoints}
                  />

                  {hasValidPostalCode && !pickupPoints.length && !isLoading && (
                    <div className="of-place-order-delivery-method__no-results">{t.L7CZ(noSearchResults)}</div>
                  )}
                </>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};
